/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarStar,
  faChartPieSimpleCircleDollar,
  faMessageLines,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { faMinus } from '@fortawesome/pro-light-svg-icons';
import { ComponentPropsWithoutRef, CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import { Rechtshinweis } from '../entities/Rechtshinweis';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@e2n/ui';
import { useTranslation } from 'react-i18next';
import { Zuschlag } from '../entities/Zuschlag';
import {
  getWiederholungStringFromTerminWiederholung,
  Termin,
} from '@/domain/arbeitszeiten/entities/Termin';
import { markierungMapper } from '@/utils';
import { Avatar, AvatarFallback, AvatarImage, Badge } from '@e2n/ui';
import { backgroundMapper } from '@/utils/backgroundColor';
import { truncatNumberToTwoDigits } from '@/domain/arbeitszeiten/shared/util';
import { DurationDisplay } from '@/domain/arbeitszeiten/shared/util/durationDisplay';

export function HinweiseCellContent({
  rechtshinweise,
  zuschlaege,
  termin,
  bemerkung,
}: {
  rechtshinweise: Rechtshinweis[];
  zuschlaege: Zuschlag[];
  termin?: Termin;
  bemerkung?: string;
}) {
  return (
    <div className="flex gap-2 pe-4">
      <Rechtshinweise rechtshinweise={rechtshinweise} />
      <Zuschlaege zuschlaege={zuschlaege} />
      <Termine termin={termin} />
      <Bemerkung bemerkung={bemerkung} />
    </div>
  );
}

function PlaceholderBadge({
  children,
  className = '',
  ...props
}: Omit<ComponentPropsWithoutRef<typeof Badge>, 'variant'>) {
  return (
    <Badge
      variant="secondary"
      className={cn(
        'text-neutrals-600 bg-neutrals-200 hover:bg-neutrals-200 px-2 py-1.5 space-x-2 h-[35px]',
        className,
      )}
      {...props}>
      {children}
    </Badge>
  );
}

type StyleProps = CSSProperties & {
  '--dot-color': CSSProperties['color'];
};

function Rechtshinweise({
  rechtshinweise,
}: {
  rechtshinweise: Rechtshinweis[];
}) {
  const { t } = useTranslation();
  if (rechtshinweise.length < 1) {
    return <RechtshinweisePlaceholder />;
  }
  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger>
          <Badge
            variant="secondary"
            className="px-2 py-1.5 space-x-2 w-[60px] bg-primary-red-100 hover:bg-primary-red-200">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              size="xl"
              className="text-primary-red-400"
            />
            <span className="text-black text-bold">
              {rechtshinweise.length}
            </span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="flex flex-col gap-2">
            <span className="font-bold uppercase tracking-widespace-y-2 px-3 pt-2 text-neutrals-700">
              {rechtshinweise.length > 1
                ? t('domain.arbeitszeiten.uebersicht.tooltips.rechtshinweise')
                : t('domain.arbeitszeiten.uebersicht.tooltips.rechtshinweis')}
            </span>
            {rechtshinweise.map((rechtshinweis, id) => {
              const dotColor = backgroundMapper[rechtshinweis.prioritaet].base;

              return (
                <div
                  className="flex border-b border-neutrals-300 last:border-none p-5 gap-5 items-center max-w-[400px] tracking-wide"
                  key={id}>
                  <Badge
                    className={`flex items-center justify-center w-24 h-8 text-xs font-semibold rounded-md bg-(--dot-color) hover:bg-(--dot-color) text-white`}
                    style={
                      {
                        '--dot-color': dotColor,
                      } as StyleProps
                    }>
                    {rechtshinweis.art}
                  </Badge>
                  <span>{rechtshinweis.name}</span>
                </div>
              );
            })}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function RechtshinweisePlaceholder() {
  return (
    <PlaceholderBadge className="w-[60px]">
      <FontAwesomeIcon icon={faTriangleExclamation} size="xl" />
      <FontAwesomeIcon
        className="text-bold text-black"
        icon={faMinus}
        size="sm"
      />
    </PlaceholderBadge>
  );
}

function ZuschlaegePlaceholder() {
  return (
    <PlaceholderBadge className="w-[60px]">
      <FontAwesomeIcon icon={faChartPieSimpleCircleDollar} size="xl" />
      <FontAwesomeIcon
        className="text-bold text-black"
        icon={faMinus}
        size="sm"
      />
    </PlaceholderBadge>
  );
}

function Zuschlaege({ zuschlaege }: { zuschlaege: Zuschlag[] }) {
  const { t } = useTranslation();
  if (zuschlaege.length < 1) {
    return <ZuschlaegePlaceholder />;
  }
  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger>
          <Badge
            className="px-2 py-1.5 space-x-2 bg-neutrals-200 w-[60px]"
            variant="secondary">
            <FontAwesomeIcon
              icon={faChartPieSimpleCircleDollar}
              size="xl"
              className="text-primary-blue-600"
            />
            <span className="text-black text-bold">{zuschlaege.length}</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="w-[400px]">
          <div className="font-bold uppercase tracking-wide text-sm text-neutrals-700 space-y-2 px-3 pt-2">
            {t('domain.arbeitszeiten.uebersicht.tooltips.zuschlaege')}
          </div>
          {zuschlaege.map((zuschlag, index) => {
            return (
              <div
                key={index}
                className={`flex gap-8 max-w-full p-5 font-bold tracking-wide ${index !== zuschlaege.length - 1 ? 'border-b border-neutrals-300' : ''}`}>
                <div className="w-3/4">
                  <span className="truncate">{zuschlag.name}</span>
                </div>
                <DurationDisplay duration={zuschlag.dauer} />
                {zuschlag.betrag && (
                  <div className="w-1/6 flex gap-1">
                    <span> {truncatNumberToTwoDigits(zuschlag.betrag)}</span>
                    <span>{t('general.currency.EUR')}</span>
                  </div>
                )}
              </div>
            );
          })}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function TerminPlaceholder() {
  return (
    <PlaceholderBadge className="w-[40px]">
      <FontAwesomeIcon icon={faCalendarStar} size="xl" />
    </PlaceholderBadge>
  );
}

function Termine({ termin }: { termin?: Termin }) {
  const { t } = useTranslation();

  if (!termin) {
    return <TerminPlaceholder />;
  }

  let beschreibung = (
    <Badge className="bg-gray-200 hover:bg-gray-200 text-gray-500 mt-2">
      {t('domain.arbeitszeiten.uebersicht.termine.beschreibungFehlend')}{' '}
    </Badge>
  );
  type StyleProps = CSSProperties & {
    '--border-color': CSSProperties['color'];
  };

  if (termin.beschreibung && termin.beschreibung.length > 0) {
    beschreibung = (
      <p className="font-normal line-clamp-2">{termin.beschreibung}</p>
    );
  }
  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger>
          <Badge
            className="px-2 py-1.5 space-x-2 bg-neutrals-200 w-[40px]"
            variant="secondary">
            <FontAwesomeIcon
              icon={faCalendarStar}
              size="xl"
              className="text-primary-blue-600"
            />
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="w-96 h-24 p-4 mr-4">
          <div className="flex gap-4">
            <Avatar
              className="w-16 h-16 border-2 border-(--border-color) bg-transparent"
              style={
                {
                  '--border-color': termin.markierung
                    ? markierungMapper[termin.markierung].base
                    : '',
                } as StyleProps
              }>
              <AvatarImage src={termin.bildUrl} alt={termin.bildName} />
              <AvatarFallback className="bg-transparent">
                <FontAwesomeIcon icon={faCalendarStar} size="lg" />
              </AvatarFallback>
            </Avatar>
            <div>
              <span>
                <span className="font-bold">{termin.titel}</span> -{' '}
                {getWiederholungStringFromTerminWiederholung(
                  termin.wiederholung,
                )}
              </span>
              {beschreibung}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function BemerkungPlaceholder() {
  return (
    <PlaceholderBadge className="w-[40px]">
      <FontAwesomeIcon icon={faMessageLines} size="xl" />
    </PlaceholderBadge>
  );
}

function Bemerkung({ bemerkung }: { bemerkung?: string }) {
  const { t } = useTranslation();

  if (!bemerkung || bemerkung.length < 1) {
    return <BemerkungPlaceholder />;
  }
  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger>
          <Badge
            className="px-2 py-1.5 space-x-2 bg-neutrals-200 w-[40px]"
            variant="secondary">
            <FontAwesomeIcon
              icon={faMessageLines}
              size="xl"
              className="text-primary-blue-600"
            />
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="w-96 h-24 p-4 mr-4">
          <div className="flex items-start gap-6 font-semibold p-1">
            <div className="text-sm text- space-y-2">
              <div className="font-bold text-neutrals-700 uppercase tracking-wide">
                {t('domain.arbeitszeiten.uebersicht.tooltips.bemerkung')}
              </div>
              <p className="text-neutrals-900">{bemerkung}</p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
